<script setup>
import { onMounted, ref, computed } from 'vue'
import Commons from '@/shared/utils/commons'
import TableList from '../others/TableList.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import RiderDetails from '../riders/RiderDetails.vue'
import RiderMapTracking from '../riders/RiderMapTracking.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import MultiDataFetcher from '@/features/data/presentation/components/MultiDataFetcher.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import { refreshBalances } from '@/app/boot.js'
import { openChatWithRider } from '@/features/chat/chatUtils.js'
import { getRiderUnreadCount } from '@/features/chat/chatState.js'
import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'

const balancesLoading = ref(true)
const updateStatusTrigger = ref(null)
const viewMode = ref('list') // 'list' | 'map'
const mapRiders = ref([])
const mapDataLoading = ref(true)
const mapRefreshFn = ref(null)
const showAddModal = ref(false)
const isAdding = ref(false)

const newRider = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  vehicleType: 'motorcycle',
  vehiclePlate: '',
})

async function handleAddRider() {
  isAdding.value = true
  try {
    const url = buildApiUrl(ADMIN_ENDPOINTS.ADMIN_RIDERS)
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRider.value),
    })
    const data = await resp.json()
    if (data.success) {
      showAddModal.value = false
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Rider ajouté avec succès',
      })
      // Reset form
      newRider.value = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        vehicleType: 'motorcycle',
        vehiclePlate: '',
      }
      // Refresh list
      Events.dispatch('RIDER_UPDATED')
    } else {
      throw new Error(data.error?.message || 'Erreur lors de l’ajout')
    }
  } catch (e) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: e.message,
    })
  } finally {
    isAdding.value = false
  }
}

onMounted(async () => {
  await refreshBalances()
  balancesLoading.value = false
})

// Fusionne les données utilisateurs et les données live quand MultiDataFetcher a tout chargé
function onMapDataFetched([users, liveResult]) {
  const userList = Array.isArray(users?.result) ? users.result : []
  const liveDataList = liveResult?.result || []

  mapRiders.value = userList.map(u => {
    const liveData = liveDataList.find(r => r.userId === u.id) || {}
    const firstName = u.profile?.firstName || u.firstName || ''
    const lastName = u.profile?.lastName || u.lastName || ''
    return {
      id: u.id,
      name: (u.name || `${firstName} ${lastName}`).trim() || 'Livreur sans nom',
      phoneNumber: u.phone || u.phoneNumber || u.profile?.phone || '',
      status: liveData.status || u.status || 'offline',
      location: {
        lat: liveData.lat ?? u.location?.lat ?? null,
        lng: liveData.lng ?? u.location?.lon ?? u.location?.lng ?? null,
        updatedAt:
          liveData.lastLocationUpdateDate ?? u.location?.updatedAt ?? null,
      },
    }
  })
}

function switchView(mode) {
  viewMode.value = mode
}

const cols = computed(() => [
  {
    name: 'Rider',
    id: 'name',
    value(el) {
      const isOnline = el.status === 'online'
      const isSuspended = Boolean(el.isSuspended || el.metadata?.isSuspended || el.metadata?.suspended)
      const dotColor = isSuspended ? 'bg-red-500' : isOnline ? 'bg-green-500' : 'bg-gray-300'
      const statusCircle = `<div class="w-2.5 h-2.5 rounded-full ${dotColor}"></div>`
      const suspendedBadge = isSuspended ? `<span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">Suspendu</span>` : ''
      return [
        `<div class="flex items-center gap-2">${statusCircle} <span class="font-medium">${el.name || '-'}</span> ${suspendedBadge}</div>`,
        el.email || el.phoneNumber || '',
      ]
    },
  },
  {
    name: 'Statut',
    id: 'status',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tout', value: '' },
        { name: 'En ligne', value: 'online' },
        { name: 'Hors ligne', value: 'offline' },
      ],
    },
    value(el) {
      const isOnline = el.status === 'online'
      const isSuspended = Boolean(el.isSuspended || el.metadata?.isSuspended || el.metadata?.suspended)
      if (isSuspended) {
        return `<span class="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">Suspendu</span>`
      }
      if (isOnline) {
        return `<span class="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">En ligne</span>`
      }
      return `<span class="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200">Hors ligne</span>`
    },
  },
  {
    name: 'Solde',
    id: 'balance',
    value(el) {
      if (el.balance === null || el.balance === undefined) return '—'
      return Commons.formatNumber(el.balance, { suffix: ' FCFA' })
    },
  },
  {
    name: 'Note',
    id: 'note',
    value(el) {
      return el.note?.toFixed(1) || '0.0'
    },
  },
  {
    name: 'Livraisons',
    id: 'stats',
    value(el) {
      return `${el.totalOrdersDelivered || 0}`
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/chat.svg',
    badge: (el) => getRiderUnreadCount(el),
    onClick(el) {
      openChatWithRider(el)
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options(el) {
      return [
        { name: 'Voir détails', value: 'view', icon: 'pi pi-eye' },
      ]
    },
    onClick(opt, el) {
      const action = typeof opt === 'object' ? opt.value : opt
      if (action === 'view') {
        detailData.value = el
      } else if (action === 'chat') {
        openChatWithRider(el)
      }
    },
    value() {},
  },
])

const detailData = ref(null)

async function handleDetailRefresh() {
  Events.dispatch('RIDER_UPDATED')
  if (detailData.value?.id || detailData.value?.riderId) {
    try {
      const rId = detailData.value.riderId || detailData.value.metadata?.riderId || detailData.value.id
      const res = await fetch(`https://api.monrapidoo.com/rider/details/${rId}?group=ultraxeno`)
      const json = await res.json()
      if (json?.data) {
        detailData.value = {
          ...detailData.value,
          ...json.data,
          status: json.data.status || detailData.value.status,
          metadata: { ...(detailData.value.metadata || {}), ...(json.data.metadata || {}) },
        }
      }
    } catch (e) {
      console.warn('Failed to refresh rider details:', e)
    }
  }
}

const onStatusSuccess = () => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Statut du livreur mis à jour',
  })
}

const onStatusError = err => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: err?.message || 'Impossible de mettre à jour le statut',
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <div v-if="balancesLoading" class="flex items-center justify-center h-64">
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"
        ></div>
        <p class="text-gray-600">Chargement des soldes...</p>
      </div>
    </div>

    <div v-else>
      <!-- Header -->
      <div
        class="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div>
          <h1 class="text-3xl font-bold text-gray-800 mb-1">
            Gestion des Riders
          </h1>
          <p class="text-gray-500 text-sm">
            Suivez les livreurs en temps réel, gérez leurs comptes et
            communiquez avec eux
          </p>
        </div>

        <div class="flex items-center gap-3">
          <button
            @click="showAddModal = true"
            class="flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
          >
            <i class="pi pi-plus"></i>
            <span>Ajouter un Rider</span>
          </button>
        </div>

        <!-- View Toggle -->
        <div
          class="flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl shadow-sm"
        >
          <button
            id="riders-view-list"
            @click="switchView('list')"
            class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            :class="
              viewMode === 'list'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            "
          >
            <i class="pi pi-list text-sm"></i>
            <span>Liste</span>
          </button>
          <button
            id="riders-view-map"
            @click="switchView('map')"
            class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            :class="
              viewMode === 'map'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            "
          >
            <i class="pi pi-map text-sm"></i>
            <span>Carte</span>
          </button>
        </div>
      </div>

      <!-- List View -->
      <div v-show="viewMode === 'list'">
        <TableList
          title="Liste des Livreurs"
          dataKey="riders"
          hasSearch
          :cols="cols"
          :refreshOnEvents="['RIDER_UPDATED']"
        ></TableList>
      </div>

      <!-- MultiDataFetcher invisible : charge les données riders en arrière-plan -->
      <div class="hidden">
        <MultiDataFetcher
          :dataKeys="['riders', 'ridersLive']"
          :filters="[{ limit: 100 }, { limit: 100 }]"
          @fetched="
            ({ data }) => {
            onMapDataFetched(data)
            mapDataLoading = false
          }
        "
        v-slot="{ loading, refresh }"
      >
        <!-- On capture loading et refresh dans des refs pour les passer à la vue map -->
        <template v-if="(mapDataLoading = loading) || true" />
        <template v-if="(mapRefreshFn = refresh) || true" />
      </MultiDataFetcher>
      </div>

      <!-- Map View : v-if pour que RiderMapTracking monte dans un container visible -->
      <div
        v-if="viewMode === 'map'"
        class="h-[calc(100vh-250px)] flex flex-col min-h-[600px]"
      >
        <div
          v-if="mapDataLoading"
          class="flex items-center justify-center h-64"
        >
          <div class="text-center">
            <div
              class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-3"
            ></div>
            <p class="text-gray-500 text-sm">Chargement des positions...</p>
          </div>
        </div>
        <div v-else class="space-y-4 h-full">
          <!-- Stats bar -->
          <div class="grid grid-cols-3 gap-3">
            <div
              class="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex items-center gap-3"
            >
              <div
                class="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center"
              >
                <span
                  class="w-3 h-3 rounded-full bg-green-500 inline-block"
                ></span>
              </div>
              <div>
                <p class="text-xl font-bold text-gray-800">
                  {{ mapRiders.filter(r => r.status === 'online').length }}
                </p>
                <p class="text-xs text-gray-500">En ligne</p>
              </div>
            </div>
            <div
              class="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex items-center gap-3"
            >
              <div
                class="w-8 h-8 bg-orange-50 rounded-full flex items-center justify-center"
              >
                <span
                  class="w-3 h-3 rounded-full bg-orange-400 inline-block"
                ></span>
              </div>
              <div>
                <p class="text-xl font-bold text-gray-800">
                  {{ mapRiders.filter(r => r.status === 'busy').length }}
                </p>
                <p class="text-xs text-gray-500">En course</p>
              </div>
            </div>
            <div
              class="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex items-center gap-3"
            >
              <div
                class="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center"
              >
                <i class="pi pi-map-marker text-indigo-500 text-sm"></i>
              </div>
              <div>
                <p class="text-xl font-bold text-gray-800">
                  {{ mapRiders.filter(r => r.location?.lat).length }}
                </p>
                <p class="text-xs text-gray-500">Localisés</p>
              </div>
            </div>
          </div>

          <div
            class="h-full flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden relative shadow-sm"
          >
            <RiderMapTracking :riders="mapRiders" />
          </div>

          <button
            @click="mapRefreshFn && mapRefreshFn()"
            class="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <i class="pi pi-refresh"></i>
            Actualiser les positions
          </button>
        </div>
      </div>
    </div>

    <ActionTrigger
      ref="updateStatusTrigger"
      dataKey="updateRiderStatus"
      @success="onStatusSuccess"
      @failed="onStatusError"
    />

    <SlideDetailView :isVisible="!!detailData" @hide="detailData = null">
      <RiderDetails
        v-if="detailData"
        :riderData="detailData"
        @open-chat="openChatWithRider(detailData)"
        @close="detailData = null"
        @update:riderData="detailData = $event"
        @refresh="handleDetailRefresh"
        @toggle-status="
          updateStatusTrigger.trigger({
            id: detailData.riderId || detailData.id,
          })
        "
      />
    </SlideDetailView>

    <!-- Add Rider Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    >
      <div
        class="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
      >
        <div
          class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50"
        >
          <h2 class="text-xl font-bold text-gray-900">Nouveau Rider</h2>
          <button
            @click="showAddModal = false"
            class="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <form @submit.prevent="handleAddRider" class="p-6 space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700 ml-1">Prénom</label>
              <input
                v-model="newRider.firstName"
                type="text"
                required
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
                placeholder="Jean"
              />
            </div>
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700 ml-1">Nom</label>
              <input
                v-model="newRider.lastName"
                type="text"
                required
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
                placeholder="Dupont"
              />
            </div>
          </div>

          <div class="space-y-1.5">
            <label class="text-sm font-bold text-gray-700 ml-1">Email</label>
            <input
              v-model="newRider.email"
              type="email"
              required
              class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
              placeholder="jean@mail.com"
            />
          </div>

          <div class="space-y-1.5">
            <label class="text-sm font-bold text-gray-700 ml-1"
              >Téléphone</label
            >
            <input
              v-model="newRider.phone"
              type="tel"
              required
              class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
              placeholder="+229 00000000"
            />
          </div>

          <div class="space-y-1.5">
            <label class="text-sm font-bold text-gray-700 ml-1"
              >Mot de passe (optionnel)</label
            >
            <input
              v-model="newRider.password"
              type="password"
              class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
              placeholder="••••••••"
            />
            <p class="text-[10px] text-gray-400 ml-1 italic">
              Défaut: Rapidoo2024
            </p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700 ml-1"
                >Véhicule</label
              >
              <select
                v-model="newRider.vehicleType"
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
              >
                <option value="motorcycle">Moto</option>
                <option value="scooter">Scooter</option>
                <option value="bicycle">Vélo</option>
                <option value="car">Voiture</option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700 ml-1">Plaque</label>
              <input
                v-model="newRider.vehiclePlate"
                type="text"
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white outline-none transition-all"
                placeholder="AB 1234"
              />
            </div>
          </div>

          <div class="pt-4 flex gap-3">
            <button
              type="button"
              @click="showAddModal = false"
              class="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-all"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="isAdding"
              class="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold font-semibold transition-all shadow-lg shadow-primary-200 flex items-center justify-center gap-2"
            >
              <i v-if="isAdding" class="pi pi-spin pi-spinner"></i>
              <span>{{ isAdding ? 'Création...' : 'Créer Rider' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
